import { Button, Field, FieldError, FieldLabel, Input } from '@arcadiapower/design-system';
import { Organization, Provider, SmtMeterSubmission } from '@schema/schemas';
import { useEffect, useMemo } from 'react';
import { Control, UseControllerProps, useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { parseErrorMessage } from '@client/config/errors';
import { trackEvent } from '@client/utils/analytics';
import { ty } from '@client/styles/typography';
import { ContentSectionFullHeight, Footer } from '../connect-container';
import { Form } from '../form';
import { Disclaimer } from '../disclaimer';
import { OrganizationTerms } from '../organization-terms';
import { Stack, Fields } from './smt-meter-form.style';

type InputProps = {
  fieldName: 'meterNumber' | 'retailCustomerEmail' | 'electricServiceIdentifier';
  control: Control<any>;
};

const ReactHookFormInput = ({ fieldName, control }: InputProps) => {
  const { t } = useTranslation('connect', { keyPrefix: 'components.smtMeterForm' });
  const label = t(`${fieldName}`);

  const rules: UseControllerProps['rules'] = useMemo(() => {
    const result: UseControllerProps['rules'] = {};
    result.required = t('validations.fieldRequiredErrorMessage', { fieldName: label });
    if (fieldName === 'electricServiceIdentifier') {
      result.minLength = {
        value: 17,
        message: t('validations.fieldLengthErrorMessage'),
      };
      result.maxLength = {
        value: 22,
        message: t('validations.fieldLengthErrorMessage'),
      };
    } else if (fieldName === 'retailCustomerEmail') {
      result.pattern = {
        value:
          // eslint-disable-next-line
          /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        message: t('validations.fieldValidEmailErrorMessage'),
      };
    }
    return result;
  }, [label, t, fieldName]);

  const { field, fieldState } = useController({
    name: fieldName,
    control,
    rules,
  });

  useEffect(() => {
    if (fieldState.isDirty) {
      trackEvent(`meters-field-modified-${fieldName}`, {
        label,
        backendField: fieldName,
      });
    }
  }, [fieldState.isDirty, fieldName, label]);

  const minLength = fieldName === 'electricServiceIdentifier' ? 17 : 0;
  const maxLength = fieldName === 'electricServiceIdentifier' ? 22 : 255;
  const errorMessage = fieldState.error?.message;
  return (
    <Field data-invalid={errorMessage ? true : undefined}>
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <Input
        id={field.name}
        maxLength={maxLength}
        minLength={minLength}
        aria-invalid={errorMessage ? true : undefined}
        type="text"
        onChange={event => field.onChange(event.target.value)}
        onBlur={field.onBlur}
        value={field.value ?? ''}
        name={field.name}
      />
      {errorMessage ? <FieldError>{errorMessage}</FieldError> : null}
    </Field>
  );
};

export type Props = {
  onSubmit: (smtMeterFields: SmtMeterSubmission) => void;
  organization: Organization;
  provider: Provider;
  error?: unknown;
  pending: boolean;
};

export const SmtMeterForm = ({
  onSubmit,
  organization,
  provider,
  error,
  pending,
}: Props): JSX.Element => {
  const { t } = useTranslation('connect', { keyPrefix: 'components.smtMeterForm' });
  const { handleSubmit, control } = useForm<SmtMeterSubmission>({
    defaultValues: {
      retailCustomerEmail: '',
      electricServiceIdentifier: '',
      meterNumber: '',
    },
  });
  return (
    <>
      <ContentSectionFullHeight>
        <Stack>
          <h3 className={ty('heading900')} data-testid="providerName">
            {provider.name}
          </h3>
          <p className={ty('default')}>{t('description', { providerName: provider.name })}</p>
          <Form onSubmit={handleSubmit(data => onSubmit(data))} id="metersForm">
            <Fields>
              <ReactHookFormInput fieldName="retailCustomerEmail" control={control} />
              <ReactHookFormInput fieldName="meterNumber" control={control} />
              <ReactHookFormInput fieldName="electricServiceIdentifier" control={control} />
              {!!error && <FieldError>{parseErrorMessage(error, '', true)}</FieldError>}
            </Fields>
          </Form>
          <Disclaimer />
        </Stack>
      </ContentSectionFullHeight>
      <Footer>
        <OrganizationTerms organization={organization} />
        <Button
          type="submit"
          loading={pending}
          className="w-full"
          form="metersForm"
          variant="inverse"
        >
          {t('submit')}
        </Button>
      </Footer>
    </>
  );
};
